import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setOptions, setSearchFilters } from "../redux/slices/search.slice";
import { buildURL, buildSearchObj, processContentFilters } from '../utils/searchFunctions';
import Filters from '../components/filters';
import ResultsSection from '../components/resultsSection';
import { searchResult, searchResultAppend, searchFilters, fetchPostTypePageHeading, getSavedContentIds } from '../redux/slices/searchActions';

export default function Search() {
  const dispatch = useDispatch();
  const abortControllerRef = useRef(null);

  const postTypeHeading = useSelector(state => state.search.fetchPostTypePageHeading);
  const searchOptions = useSelector(state => state.search.searchOptions);
  const savedContentIds = useSelector((state) => state.search.getSavedContentIds);
  const showFilterLoading = useSelector(state => state.search.showFilterLoading);
  const showResultsLoading = useSelector(state => state.search.showResultsLoading);
  const isLoadMore = useSelector(state => state.search.isLoadMore);
  const isExpandingSearch = useSelector(state => state.search.isExpandingSearch);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const allFilters = useSelector(state => state.search.searchFilters);
  const hasFetchedSavedContent = useRef(false);
  const hasFetchedPostTypeHeading = useRef(false);

  const resultsManagmentSection = [1, 2, 3, 4];

  useEffect(() => {
    if (!savedContentIds || !savedContentIds.post_ids?.length) {
      if (hasFetchedSavedContent.current) return;
      hasFetchedSavedContent.current = true;
      fetchSavedContentIds();
    }
    if (!hasFetchedPostTypeHeading.current) {
      dispatch(fetchPostTypePageHeading());
      hasFetchedPostTypeHeading.current = true;
    }
  }, [dispatch, savedContentIds, searchOptions.contentType, searchOptions.searchQuery, postTypeHeading]);

  const fetchSavedContentIds = async () => {
    try {
      const userId = window.current_user_id || 2;
      await dispatch(getSavedContentIds({ userId }));
    } catch (error) {
      console.error("Error fetching saved content IDs:", error);
    }
  };

  useEffect(() => {
    const fetchSearchData = async (options) => {
      const abortControllerMain = new AbortController();
      const [updateSearchFilters, updateSearchResult] = await Promise.all([
        dispatch(searchFilters(options)).unwrap(),
        dispatch(searchResult({ data: options, signal: abortControllerMain.signal })).unwrap()
      ]);

      if (updateSearchFilters.success && updateSearchResult.success) {
        const currentFilters = JSON.parse(JSON.stringify(updateSearchFilters.data));
        const filters = processContentFilters(updateSearchResult.data, currentFilters, options);
        dispatch(setSearchFilters(filters));
      }
    };
    //Instead of getting data from base object can do ajax calls to get initial data here
    const searchParams = new URLSearchParams(window.location.search);
    const options = buildSearchObj(searchParams);
    const updateOptions = {...options, isPublicSearch: false, userRoles: window.user_roles || ''};
    dispatch(setOptions(updateOptions));
    fetchSearchData(updateOptions);
  }, []);

  const handleFilterChange = async (options) => {
    // Reset pagination when clearing filters
    if (!options.loadMore && !options.expandingSearch) {
      options = {
        ...options,
        skip: 0,
        take: 8
      };
    }

    // Cancel the previous request if it exists
    if (abortControllerRef.current && !options.loadMore) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController for the current request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    dispatch(setOptions(options));
    const urlOptions = buildURL(options);

    if (window) {
      window.history.pushState(null, null, `${window.location.pathname}${urlOptions != '' ? `?${urlOptions}` : ""}`);
    }

    try {
      let updateSearchResult = null;
      if (options.loadMore || options.expandingSearch) {
        updateSearchResult = await dispatch(
          searchResultAppend({ data: options, signal: abortController.signal })
        ).unwrap();
      }
      else {
        updateSearchResult = await dispatch(
          searchResult({ data: options, signal: abortController.signal })
        ).unwrap();
      }
  
      if (updateSearchResult.success) {
        const currentFilters = JSON.parse(JSON.stringify(allFilters));
        const filters = processContentFilters(
          updateSearchResult.data,
          currentFilters,
          options
        );
        dispatch(setSearchFilters(filters));
      }
    } catch (error) {
      // if (error.name === 'AbortError') {
      //   console.log('Previous request aborted');
      // } else {
      //   console.error('Error fetching search results:', error);
      // }
    }
  };

  return (
    <div id="maincontent">
      <div className={`resultsContainer`}>
        <Filters
          isFilterActive={isFilterActive}
          filterClose={() => setIsFilterActive(false)}
          onFilterChange={(options) => handleFilterChange(options)}
        />

        {showFilterLoading || showResultsLoading ? (
          <div className="resultsManagmentSection placeholder-glow">
            <span className="resultsHeading placeholder col-2"></span>
            <div>
            {resultsManagmentSection.map((index) => (
              <div className="resultsManagmentCard" key={index}>
                <div className="resultsManagmentCardContent">
                  <div className="report-bar-icons">
                    <span className="placeholder col-4" style={{width:"4em"}}></span>
                  </div>
                  <span className="placeholder col-1 me-3"></span>
                  <span className="placeholder col-2"></span>
                  <div>
                    <div>
                      <span className="placeholder col-12 reportTitle mb-2"></span>
                      <div>
                        <span className="placeholder col-12" style={{height:"3em"}}></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              ))}
            </div>
          </div>
        ) : (
          <ResultsSection
            filterOpen={() => setIsFilterActive(true)}
            isLoadMore={isLoadMore}
            isExpandingSearch={isExpandingSearch}
            refreshData={(options) => handleFilterChange(options)}
          />
        )}
      </div>
    </div>
  );
}